import type {
	DynamicFormSchema,
	FieldType,
	FormField,
	PersistedDynamicFieldDefinition,
	ToggleButtonField,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import { persistedDefinitionToFormField } from "@/lib/dynamicForms/utils/persisted-field-definition.util";
import {
	type LayoutElement,
	LayoutTypes,
	parseFormLayoutUnknown,
} from "@/lib/layoutRenderer/schemas/layout.schema";
import {
	buildFieldDefinitionsById,
	buildFieldDefinitionsByName,
	resolveFieldDefinitionByLayoutId,
} from "@/lib/layoutRenderer/utils/field-definition-map.util";
import { resolveLayoutFromParsedLayoutJson } from "@/services/form-definition/form-definition-layout-parse.util";
import type { FormEditorEntity } from "../form-editor-entity.types";
import type {
	BuilderFieldData,
	BuilderNode,
	PaletteFieldType,
	TicketTypeFormBuilderState,
} from "../types";
import {
	PALETTE_EMAIL_PATTERN,
	PALETTE_URL_PATTERN,
} from "./palette-field-patterns";
import { builderFieldDataFromTemplate } from "./palette-form-field-factory";
import { resolveBuilderSectionLabel } from "./resolve-builder-section-label";
import { clampSectionMaxItems } from "./section-field-limit";

export interface SchemaToBuilderHydrateResult
	extends Pick<
		TicketTypeFormBuilderState,
		"rootIds" | "nodes" | "formTitle" | "formDescription"
	> {
	selectedId: null;
	warnings: string[];
}

/**
 * Resolves the layout root from a {@link DynamicFormSchema}.
 *
 * Accepts the same shapes as wire `layoutJson` / query `select`:
 * - Root layout: `{ type: "VerticalLayout", elements: [...] }`
 * - Saved envelope: `{ title?, description?, layout, fieldDefinitions? }`
 * - JSON string of either of the above (legacy / API rows before parse)
 */
export function resolveSchemaLayout(
	schema: DynamicFormSchema,
): { ok: true; layout: LayoutElement } | { ok: false; error: string } {
	return resolveLayoutJsonValue(schema.layoutJson as unknown);
}

function resolveLayoutJsonValue(
	raw: unknown,
): { ok: true; layout: LayoutElement } | { ok: false; error: string } {
	if (raw === null || raw === undefined) {
		return { ok: false, error: "Missing layout and layoutJson" };
	}
	if (typeof raw === "string") {
		const trimmed = raw.trim();
		if (trimmed.length === 0) {
			return { ok: false, error: "Missing layout and layoutJson" };
		}
		try {
			const parsed: unknown = JSON.parse(trimmed);
			return resolveLayoutJsonValue(parsed);
		} catch (e) {
			const err = e instanceof Error ? e : new Error(String(e));
			return { ok: false, error: err.message };
		}
	}
	if (typeof raw !== "object" || Array.isArray(raw)) {
		return { ok: false, error: "layoutJson must be an object or JSON string" };
	}
	const candidate = resolveLayoutFromParsedLayoutJson(
		raw as Record<string, unknown>,
	);
	if (
		candidate === null ||
		typeof candidate !== "object" ||
		Array.isArray(candidate)
	) {
		return { ok: false, error: "Could not resolve layout from layoutJson" };
	}
	const parsed = parseFormLayoutUnknown(candidate);
	if (!parsed.ok) {
		return { ok: false, error: parsed.error.message };
	}
	return { ok: true, layout: parsed.layout };
}

/**
 * Maps layout `layoutInfo.fieldType` to a builder palette id when it matches the persisted field.
 * When this returns non-null, it takes precedence over {@link inferPaletteFieldType}
 * (e.g. `textarea` in layout + `text` in the definition → `"textarea"`).
 */
export function paletteTypeFromLayoutInfo(
	field: FormField,
	layoutFieldType: FieldType | undefined,
	warnings: string[],
): PaletteFieldType | null {
	if (layoutFieldType === undefined) return null;

	if (layoutFieldType === "textarea") {
		if (field.type !== "text") {
			warnings.push(
				`Layout fieldType "textarea" ignored for field "${field.label}" (definition type is "${field.type}").`,
			);
			return null;
		}
		const pattern = field.validation?.pattern;
		if (pattern === PALETTE_EMAIL_PATTERN || pattern === PALETTE_URL_PATTERN) {
			return null;
		}
		return "textarea";
	}

	if (layoutFieldType === "toggleButton") {
		if (field.type !== "toggleButton") {
			warnings.push(
				`Layout fieldType "toggleButton" ignored for field "${field.label}" (definition type is "${field.type}").`,
			);
			return null;
		}
		const tb = field as ToggleButtonField;
		if (tb.exclusive === false) return "checkbox";
		return null;
	}

	if (layoutFieldType === "text") {
		if (field.type !== "text") return null;
		return inferPaletteFieldType(field, warnings);
	}

	if (layoutFieldType === "checkbox") {
		if (field.type !== "checkbox") {
			warnings.push(
				`Layout fieldType "checkbox" ignored for field "${field.label}" (definition type is "${field.type}").`,
			);
			return null;
		}
		return "checkbox";
	}

	const directPaletteTypes: PaletteFieldType[] = [
		"number",
		"phone",
		"url",
		"date",
		"time",
		"dateTime",
		"select",
		"radio",
		"file",
		"multiSelect",
		"users",
		"signature",
		"address",
	];
	const asPalette = layoutFieldType as PaletteFieldType;
	if (directPaletteTypes.includes(asPalette)) {
		if (field.type !== layoutFieldType) {
			warnings.push(
				`Layout fieldType "${layoutFieldType}" ignored for field "${field.label}" (definition type is "${field.type}").`,
			);
			return null;
		}
		return asPalette;
	}

	return null;
}

/**
 * Infers palette field type from a {@link FormField} for schema → builder import.
 * For `type: "text"`, cannot distinguish `textarea` from single-line text without the layout
 * field node's `layoutInfo.fieldType`; {@link paletteTypeFromLayoutInfo} supplies that.
 */
export function inferPaletteFieldType(
	field: FormField,
	warnings: string[],
): PaletteFieldType | null {
	switch (field.type) {
		case "text": {
			const pattern = field.validation?.pattern;
			if (pattern === PALETTE_EMAIL_PATTERN) return "email";
			if (pattern === PALETTE_URL_PATTERN) return "url";
			return "text";
		}
		case "number":
			return "number";
		case "phone":
			return "phone";
		case "url":
			return "url";
		case "date":
			return "date";
		case "time":
			return "time";
		case "dateTime":
			return "dateTime";
		case "select":
			return "select";
		case "radio":
			return "radio";
		case "file":
			return "file";
		case "multiSelect":
			return "multiSelect";
		case "users":
			return "users";
		case "signature":
			return "signature";
		case "address":
			return "address";
		case "checkbox":
			return "checkbox";
		case "toggleButton": {
			const tb = field as ToggleButtonField;
			if (tb.exclusive === false) return "checkbox";
			warnings.push(
				`Skipped field "${field.label}" (toggleButton with exclusive=true is not supported in the form builder).`,
			);
			return null;
		}
		case "group":
			warnings.push(
				`Skipped nested group "${field.label}" (only one section level is supported).`,
			);
			return null;
		case "switch":
		case "slider":
		case "autocomplete":
		case "fieldArray":
			warnings.push(
				`Skipped field "${field.label}" (type "${field.type}" is not supported in the form builder).`,
			);
			return null;
		default: {
			warnings.push(
				`Skipped field "${field.label}" (type "${(field as FormField).type}" is not supported in the form builder).`,
			);
			return null;
		}
	}
}

/**
 * Maps a single schema leaf field to {@link BuilderFieldData}, or `null` if unsupported.
 *
 * @param layoutFieldType - Optional `layoutInfo.fieldType` from the layout `Field` node.
 */
export function formFieldToBuilderFieldData(
	field: FormField,
	warnings: string[],
	layoutFieldType?: FieldType,
): BuilderFieldData | null {
	const fromLayout = paletteTypeFromLayoutInfo(
		field,
		layoutFieldType,
		warnings,
	);
	const paletteType = fromLayout ?? inferPaletteFieldType(field, warnings);
	if (paletteType === null) return null;
	return builderFieldDataFromTemplate(paletteType, field);
}

/**
 * Maps a persisted definition to builder data, or `null` if unsupported.
 *
 * @param layoutFieldType - Optional `layoutInfo.fieldType` from the layout `Field` node.
 */
export function persistedDefinitionToBuilderFieldData(
	def: PersistedDynamicFieldDefinition,
	warnings: string[],
	layoutFieldType?: FieldType,
): BuilderFieldData | null {
	const formField = persistedDefinitionToFormField(def, {
		layoutFieldType,
	});
	if (!formField) {
		warnings.push(
			`Skipped field "${def.name}" (dataType "${def.dataType}" is not supported in the form builder).`,
		);
		return null;
	}
	return formFieldToBuilderFieldData(formField, warnings, layoutFieldType);
}

/**
 * Builds {@link TicketTypeFormBuilderState} graph from a persisted {@link DynamicFormSchema}.
 * Best-effort: unsupported fields are omitted and listed in `warnings`.
 */
export function dynamicFormSchemaToBuilderState(
	schema: DynamicFormSchema,
	formEditorEntity: FormEditorEntity = "ticket",
): SchemaToBuilderHydrateResult {
	const warnings: string[] = [];
	const nodes: Record<string, BuilderNode> = {};
	const rootIds: string[] = [];
	const usedIds = new Set<string>();

	const layoutResult = resolveSchemaLayout(schema);
	if (!layoutResult.ok) {
		warnings.push(layoutResult.error);
		return {
			formTitle: schema.title ?? "",
			formDescription: schema.description ?? "",
			rootIds: [],
			nodes: {},
			selectedId: null,
			warnings,
		};
	}
	const layoutRoot = layoutResult.layout;

	const byId = buildFieldDefinitionsById(schema.fieldDefinitions);
	const byName = buildFieldDefinitionsByName(schema.fieldDefinitions);

	function allocateId(desired: string): string {
		const base =
			desired.trim().length > 0
				? desired
				: typeof crypto !== "undefined" && crypto.randomUUID
					? crypto.randomUUID()
					: `id_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
		if (!usedIds.has(base)) {
			usedIds.add(base);
			return base;
		}
		let n = 2;
		let candidate = `${base}_${n}`;
		while (usedIds.has(candidate)) {
			n += 1;
			candidate = `${base}_${n}`;
		}
		usedIds.add(candidate);
		warnings.push(`Duplicate id "${base}" was renamed to "${candidate}".`);
		return candidate;
	}

	function addLeafFromDefinition(
		def: PersistedDynamicFieldDefinition,
		parentId: "root" | string,
		layoutFieldType?: FieldType,
	): void {
		const data = persistedDefinitionToBuilderFieldData(
			def,
			warnings,
			layoutFieldType,
		);
		if (!data) return;

		const nodeId = allocateId(data.id);
		const fieldNode: BuilderNode = {
			kind: "field",
			field: {
				...data,
				id: nodeId,
			},
			parentId,
		};
		nodes[nodeId] = fieldNode;

		if (parentId === "root") {
			rootIds.push(nodeId);
		} else {
			const parent = nodes[parentId];
			if (parent?.kind === "section") {
				nodes[parentId] = {
					...parent,
					childFieldIds: [...parent.childFieldIds, nodeId],
				};
			}
		}
	}

	function walkLayout(node: LayoutElement, parentId: "root" | string): void {
		if (node.type === LayoutTypes.VerticalLayout) {
			for (const el of node.elements) {
				walkLayout(el, parentId);
			}
			return;
		}

		if (node.type === LayoutTypes.HorizontalLayout) {
			warnings.push(
				"HorizontalLayout is not supported in the form builder; flattening children vertically.",
			);
			for (const el of node.elements) {
				walkLayout(el, parentId);
			}
			return;
		}

		if (node.type === LayoutTypes.Group) {
			if (parentId !== "root") {
				warnings.push(
					`Skipped nested group "${node.label ?? "(untitled)"}" (only one section level is supported).`,
				);
				return;
			}
			const desiredSectionId =
				node.id?.trim() ||
				`sec_${rootIds.length}_${(node.label ?? "section").replace(/\s+/g, "_").slice(0, 24)}`;
			const sectionId = allocateId(desiredSectionId);
			const maxItems = clampSectionMaxItems(node.maxItems);

			nodes[sectionId] = {
				kind: "section",
				section: {
					id: sectionId,
					label: resolveBuilderSectionLabel(node.label ?? "", formEditorEntity),
					helpText: "",
					collapsible: node.enabled === true,
					defaultExpanded: node.defaultExpanded,
					maxItems,
				},
				childFieldIds: [],
			};
			rootIds.push(sectionId);

			const groupElements =
				maxItems != null
					? node.elements.slice(0, Math.max(0, maxItems))
					: node.elements;
			for (const el of groupElements) {
				walkLayout(el, sectionId);
			}
			return;
		}

		if (node.type === LayoutTypes.Field) {
			const def = resolveFieldDefinitionByLayoutId(
				node.fieldDefinitionId,
				byId,
				byName,
			);
			if (!def) {
				warnings.push(
					`No field definition for layout id "${node.fieldDefinitionId}".`,
				);
				return;
			}
			addLeafFromDefinition(def, parentId, node.layoutInfo?.fieldType);
		}
	}

	if (layoutRoot.type === LayoutTypes.VerticalLayout) {
		for (const el of layoutRoot.elements) {
			walkLayout(el, "root");
		}
	} else {
		walkLayout(layoutRoot, "root");
	}

	return {
		formTitle: schema.title ?? "",
		formDescription: schema.description ?? "",
		rootIds,
		nodes,
		selectedId: null,
		warnings,
	};
}

/**
 * Returns true if `value` is a persisted dynamic form envelope (fieldDefinitions + layout).
 */
export function isLooseDynamicFormSchema(
	value: unknown,
): value is DynamicFormSchema {
	if (value === null || typeof value !== "object") return false;
	const o = value as Record<string, unknown>;
	if (!Array.isArray(o.fieldDefinitions)) return false;
	if (
		o.layoutJson !== null &&
		typeof o.layoutJson === "object" &&
		!Array.isArray(o.layoutJson)
	) {
		return true;
	}
	if (typeof o.layoutJson === "string" && o.layoutJson.trim().length > 0)
		return true;
	return false;
}
